"use client";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  generateObjectId,
  getLoggedinUser,
  getStorage,
  readReachOutBoxComments,
  setStorage,
  unsetStorage,
} from "../../lib/util";
import { getActiveUser } from "../slice/auth-slice";
import { useAppSelector } from "../slice/hooks";
import { useClient, useToken } from "../../lib/context/auth-context";

export const useReachOutBox = (organisation, accessPoint) => {
  const disableStoreProductPrice =
    accessPoint?.settings?.disableStoreFrontPrice || false;
  const sectionName = "reachout";
  const [openChatWindow, setOpenChatWindow] = useState(false);
  const [chatGuestMode, setChatGuestMode] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [showSenderName, setShowSenderName] = useState(true);

  const [guestId, setGuestId] = useState(null);
  const [chatInfo, setChatInfo] = useState({
    imageUrl: organisation?.logoUrl,
    name: organisation?.orgName,
    sectionId: null,
    sectionName,
  });

  const { loggedInUser } = useAppSelector((state) =>
    getActiveUser(state, getLoggedinUser())
  );
  const client = useClient();

  const orgId = organisation?.orgId;
  const { token } = useToken();

  const chatUserId = chatGuestMode ? guestId : loggedInUser?.user?.userId;

  const {
    data: box = {},
    isLoading: isLoadingBox,
    isFetching: isFetchingBox,
    error: chatBoxFetchError,
    refetch: refetchBox, // 🔹 grab refetch
  } = useQuery(
    ["box", orgId, chatUserId, chatGuestMode],
    async () => {
      const endpoint =
        chatGuestMode || !chatUserId
          ? `conversation/reachout/organisation/${orgId}/guest/${chatUserId}/info`
          : `conversation/reachout/organisation/${orgId}/user/${chatUserId}/info`;

      return client(endpoint).then((res) => res.data);
    },
    {
      enabled: !!orgId && (chatGuestMode || !!chatUserId),
      keepPreviousData: true,
    }
  );

  const handleBoxRetry = useCallback(() => {
    if (refetchBox) {
      refetchBox();
    }
  }, [refetchBox]);

  // 🔹 Reset state helper
  const closeChat = () => {
    setOpenChatWindow(false);
  };
  const openChat = () => {
    setOpenChatWindow(true);
  };

  const clearPresetMessage = () => {
    setActiveProduct(null);
  };

  const activateGuestMode = (guestMode = true) => {
    setChatGuestMode(guestMode);

    if (guestMode && !guestId) {
      const saved = getStorage("guestId");
      if (saved) {
        setGuestId(saved);
      } else {
        const newId = generateObjectId();
        setStorage("guestId", newId);
        setGuestId(newId);
      }
    }

    setChatInfo((prev) => ({
      ...prev,
      sectionId: null,
      unreadMessageCount: 0,
    }));
  };

  const markCommentsAsRead = useCallback(async () => {
    try {
      if (!chatInfo?.sectionId || !token) return;
      await readReachOutBoxComments(chatInfo.sectionId, token);
    } catch (err) {
      console.error(err);
    }
  }, [chatInfo?.sectionId, token]);

  useEffect(() => {
    if (chatBoxFetchError) toast.error("Failed to fetch chat box");
  }, [chatBoxFetchError]);

  useEffect(() => {
    if (box?.box) {
      setChatInfo({
        ...box?.box,
        imageUrl: organisation?.logoUrl,
        name: organisation?.orgName,
        sectionId: box?.box?.boxId || null,
        sectionName,
        unreadMessageCount: box?.box?.chatReadCount,
      });
    }
  }, [box, organisation]);

  useEffect(() => {
    if (loggedInUser?.user && chatGuestMode === false) {
      unsetStorage("guestId");
    }
  }, [loggedInUser, chatGuestMode]);

  // derived payload (no need for extra state)

  const chatPayload = {
    userId: chatUserId || null,
    orgId: orgId || null,
    memberId: chatInfo?.memberId || null,
  };

  const presetMessage = useMemo(() => {
    if (!activeProduct) return null;

    return {
      uiPreview: {
        title: activeProduct?.name,
        image: activeProduct?.media?.[0] || null,
        price:
          !disableStoreProductPrice && activeProduct?.amount
            ? `${activeProduct?.currency} ${activeProduct?.amount}`
            : "Negotiable",
        description: activeProduct?.description || "",
      },
      apiPayload: {
        comment: `${activeProduct?.name}`,
        appName: "product",
        commentType: "APP",
        appId: activeProduct.productId,
        appAuthor: chatGuestMode
          ? {
              name: `Guest-${chatUserId?.slice(-4)}`,
              image: "",
              email: "",
            }
          : {
              name:
                `${loggedInUser?.user?.firstName ?? ""} ${
                  loggedInUser?.user?.lastName ?? ""
                }`.trim() || "Unknown",
              image: loggedInUser?.user?.image || "",
              email: loggedInUser?.user?.email || "",
            },
        appData: {
          appId: activeProduct.productId,
          title: activeProduct.name,
          amount: activeProduct.amount,
          currency: activeProduct.currency,
          productNo: activeProduct.checkSum,
        },
        ...(activeProduct?.media?.length > 0 && {
          media: activeProduct.media,
        }),
      },
    };
  }, [activeProduct, disableStoreProductPrice, loggedInUser]);

  return {
    openChatWindow,
    chatInfo,
    chatPayload,
    loggedInUser,
    isLoadingBox,
    isFetchingBox,
    chatBoxFetchError,
    handleBoxRetry,
    openChat,
    closeChat,
    markCommentsAsRead,
    setActiveProduct,
    clearPresetMessage,
    presetMessage,
    chatGuestMode,
    activateGuestMode,
    showSenderName,
  };
};
